// dwaar/src/pages/Niriksha.jsx
// Repo: terralekha-core | Folder: dwaar/src/pages/
// Niriksha — Sanskrit: निरीक्षा — inspection, supervision, oversight.
//   Founder-only control centre page inside the Dwaar portal.
//   Route: /supervisor  (guarded by Dvarapal session check + admin role gate below)
//
// Sprint 4 redesign — expanded KPI dashboard:
//   1. Hero KPI strip      — Orgs / Users / PassPorts / Backend tests / Portal tests / Sprint tasks
//   2. Infrastructure      — Component health grid (parallel async checks)
//   3. Sprint progress     — Sprint 4 task checklist + Pending actions (two-column)
//   4. Module delivery     — All 10 modules with tier, test count, status
//   5. PassPort funnel     — Draft → Submitted → Confirmed → Sealed (FSM breakdown)
//
// Data flow:
//   1. auth.getSession()                → userId + accessToken
//   2. public.users WHERE id = userId   → role (must be 'admin')
//   3. GET /api/niriksha/health         → aggregated health payload
//
// Access control:
//   - Dvarapal ensures a valid session exists.
//   - This component additionally checks role === 'admin'.
//   - Any other role sees an "Access restricted" screen — no data fetched.
//
// Layout:
//   Koshtha sidebar with role="admin" — shows Health + soon items for admin nav.
//   hideOra is not set (Ora available in sidebar for admin context questions).
//
// Reveal doctrine:
//   Internal API errors stay in console. Users (even admin) see only safe messages.

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSupabaseClient } from "../lib/supabaseClient.js";
import Koshtha from "../components/Koshtha.jsx";

// ── Config ────────────────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL ?? "";

// ── Constants ─────────────────────────────────────────────────────────────────

const PHASE = Object.freeze({ LOADING: "loading", FORBIDDEN: "forbidden", READY: "ready", ERROR: "error" });

const MODULE_META = {
  bhoomi_schema:        { label: "Bhoomi",              sanskrit: "भूमि — foundation"    },
  pravesh_magic_link:   { label: "Pravesh / Magic link", sanskrit: "प्रवेश — entry"       },
  pravesh_sms_otp:      { label: "Sandesha / SMS OTP",  sanskrit: "संदेश — message"       },
  pravesh_wa_otp:       { label: "Samvad / WA OTP",     sanskrit: "संवाद — dialogue"      },
  anumati_confirmation: { label: "Anumati",             sanskrit: "अनुमति — sanction"     },
  mudran_pdf:           { label: "Mudran",              sanskrit: "मुद्रण — printing"     },
  vakil_ora:            { label: "Vakil / Ora",         sanskrit: "वकील — counsel"        },
  dwaar_portal:         { label: "Dwaar",               sanskrit: "द्वार — gateway"       },
  aavahan_login:        { label: "Aavahan",             sanskrit: "आवाहन — invitation"    },
  niriksha_control:     { label: "Niriksha",            sanskrit: "निरीक्षा — inspection" },
};

const COMPONENT_LABELS = {
  database_supabase:        "Supabase DB",
  ai_anthropic:             "Anthropic (Ora)",
  sms_whatsapp_msg91:       "MSG91 (SMS / WA)",
  email_resend:             "Resend (Email)",
  cloudflare_verify_worker: "Cloudflare Verify Worker",
  render_api:               "Render FastAPI",
  scopeengine:              "ScopeEngine / FactorStore",
};

// PassPort FSM steps in canonical order
const FUNNEL_STEPS = [
  { key: "draft",                label: "Draft"                },
  { key: "submitted",            label: "Submitted"            },
  { key: "pending_confirmation", label: "Pending Confirmation" },
  { key: "confirmed",            label: "Confirmed"            },
  { key: "sealed",               label: "Sealed"               },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusClass(status) {
  return {
    healthy:        "tl-nk-pill--healthy",
    degraded:       "tl-nk-pill--degraded",
    unreachable:    "tl-nk-pill--degraded",
    not_configured: "tl-nk-pill--warn",
    configured:     "tl-nk-pill--info",
    partial:        "tl-nk-pill--warn",
  }[status] || "tl-nk-pill--muted";
}

function statusLabel(status) {
  return {
    healthy:        "● Healthy",
    degraded:       "● Degraded",
    unreachable:    "● Unreachable",
    not_configured: "⚠ Not configured",
    configured:     "✓ Configured",
    partial:        "⚠ Partial",
  }[status] || status;
}

// ── Sub-components ────────────────────────────────────────────────────────────

/** Single large-number KPI card in the hero strip. */
function HeroKpi({ label, value, color, ariaLabel }) {
  return (
    <div className={`tl-nk-hero-card tl-nk-hero-card--${color}`}>
      <p className="tl-nk-hero-value" aria-label={ariaLabel}>{value ?? "—"}</p>
      <p className="tl-nk-hero-label">{label}</p>
    </div>
  );
}

/** Single row in the Sprint task checklist. */
function SprintTaskRow({ task }) {
  const done = Boolean(task.done);
  return (
    <li className={`tl-nk-task-item tl-nk-task-item--${done ? "done" : "pending"}`}>
      <span className="tl-nk-task-icon" aria-hidden="true">{done ? "✓" : "○"}</span>
      <span className="tl-nk-task-label">{task.label}</span>
    </li>
  );
}

/** One step in the PassPort funnel visualisation. */
function FunnelStep({ label, count, total }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="tl-nk-funnel-step">
      <div className="tl-nk-funnel-meta">
        <span className="tl-nk-funnel-label">{label}</span>
        <span className="tl-nk-funnel-count" aria-label={`${label} count`}>{count}</span>
      </div>
      <div className="tl-nk-funnel-bar-wrap" title={`${pct}%`}>
        <div
          className="tl-nk-funnel-bar"
          style={{ width: pct > 0 ? `${pct}%` : "4px" }}
          role="progressbar"
          aria-valuenow={count}
          aria-valuemin={0}
          aria-valuemax={total}
        />
      </div>
    </div>
  );
}

/** Component health card. Shows org / passport / user counts when available. */
function ComponentCard({ name, data }) {
  const label     = COMPONENT_LABELS[name] || name;
  const hasOrgRow = data.org_count != null;
  const hasPpRow  = data.passport_count != null && !hasOrgRow;

  return (
    <div className="tl-nk-comp-card">
      <div className="tl-nk-comp-header">
        <span className="tl-nk-comp-name">{label}</span>
        <span
          className={`tl-nk-pill ${statusClass(data.status)}`}
          aria-label={`${label} status`}
        >
          {statusLabel(data.status)}
        </span>
      </div>
      {data.latency_ms != null && (
        <p className="tl-nk-comp-meta">Latency: {data.latency_ms} ms</p>
      )}
      {data.factor_count != null && (
        <p className="tl-nk-comp-meta">
          {data.factor_count} factors · v{data.version ?? "?"}
        </p>
      )}
      {hasOrgRow && (
        <p className="tl-nk-comp-meta">
          {data.org_count} org{data.org_count !== 1 ? "s" : ""} · {data.passport_count} PassPorts · {data.user_count} users
        </p>
      )}
      {hasPpRow && (
        <p className="tl-nk-comp-meta">{data.passport_count} PassPorts · {data.user_count} users</p>
      )}
      {data.action && (
        <p className="tl-nk-comp-action">→ {data.action}</p>
      )}
    </div>
  );
}

/** Module row in the delivery table. */
function ModuleRow({ modKey, mod }) {
  const meta      = MODULE_META[modKey] || { label: modKey, sanskrit: "" };
  const tierLabel = `Tier ${mod.tier_verified}`;
  return (
    <tr className="tl-nk-tr">
      <td className="tl-nk-td tl-nk-td--name">{meta.label}</td>
      <td className="tl-nk-td tl-nk-td--sanskrit">{meta.sanskrit}</td>
      <td className="tl-nk-td">
        <span
          className={`tl-nk-tier-badge tl-nk-tier-badge--${mod.tier_verified}`}
          aria-label={`Tier: ${mod.tier_verified}`}
        >
          {tierLabel}
        </span>
      </td>
      <td className="tl-nk-td tl-nk-td--tests">{mod.tests ?? "—"}</td>
      <td className="tl-nk-td">
        <span
          className={`tl-nk-pill ${statusClass(mod.status)}`}
          aria-label={`Module status: ${mod.status}`}
        >
          {mod.status.replace(/_/g, " ")}
        </span>
      </td>
      <td className="tl-nk-td tl-nk-td--note">{mod.note || ""}</td>
    </tr>
  );
}

// ── Niriksha ──────────────────────────────────────────────────────────────────

export default function Niriksha() {
  const navigate = useNavigate();

  const [phase,       setPhase]       = useState(PHASE.LOADING);
  const [health,      setHealth]      = useState(null);
  const [accessToken, setAccessToken] = useState("");
  const [refreshing,  setRefreshing]  = useState(false);
  const [lastFetched, setLastFetched] = useState(null);
  const [activeTab,     setActiveTab]     = useState("health");
  const [pricingData,   setPricingData]   = useState(null);
  const [pricingForm,   setPricingForm]   = useState(null);
  const [pricingSaving, setPricingSaving] = useState(false);
  const [passportsData,   setPassportsData]   = useState(null);
  const [registryData,    setRegistryData]    = useState(null);
  const [revenueData,     setRevenueData]     = useState(null);
  const [complianceData,   setComplianceData]   = useState(null);
  const [leadsData,        setLeadsData]        = useState(null);
  const [onboarding,       setOnboarding]       = useState({});
  const [onboardError,     setOnboardError]     = useState({});
  const [confirming,       setConfirming]       = useState({});
  const [confirmMsg,       setConfirmMsg]       = useState({});
  const [passportsLoaded,  setPassportsLoaded]  = useState(false);
  const [registryLoaded,   setRegistryLoaded]   = useState(false);
  const [revenueLoaded,    setRevenueLoaded]    = useState(false);
  const [complianceLoaded, setComplianceLoaded] = useState(false);
  const [leadsLoaded,      setLeadsLoaded]      = useState(false);

  // ── Fetch health ────────────────────────────────────────────────────────────
  const fetchHealth = useCallback(async (token) => {
    try {
      const res = await fetch(`${API_BASE}/api/niriksha/health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`health fetch ${res.status}`);
      const data = await res.json();
      setHealth(data);
      setLastFetched(new Date());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("niriksha: health fetch failed", err);
    }
  }, []);

  const fetchPricing = useCallback(async (token) => {
    try {
      const res = await fetch(`${API_BASE}/api/niriksha/pricing`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setPricingData(data);
      setPricingForm({
        per_passport_price:         data.per_passport_price,
        subscription_monthly_price: data.subscription_monthly_price,
        subscription_annual_price:  data.subscription_annual_price,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("niriksha: pricing fetch failed", err);
    }
  }, []);

  // ── Lazy tab fetches ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!accessToken) return;
    const headers = { Authorization: `Bearer ${accessToken}` };

    if (activeTab === "passports" && !passportsLoaded) {
      setPassportsLoaded(true);
      fetch(`${API_BASE}/api/niriksha/passports`, { headers })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setPassportsData(data); })
        .catch(() => {});
    }
    if (activeTab === "registry" && !registryLoaded) {
      setRegistryLoaded(true);
      fetch(`${API_BASE}/api/niriksha/registry`, { headers })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setRegistryData(data); })
        .catch(() => {});
    }
    if (activeTab === "revenue" && !revenueLoaded) {
      setRevenueLoaded(true);
      fetch(`${API_BASE}/api/niriksha/revenue`, { headers })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setRevenueData(data); })
        .catch(() => {});
    }
    if (activeTab === "compliance" && !complianceLoaded) {
      setComplianceLoaded(true);
      fetch(`${API_BASE}/api/niriksha/compliance-summary`, { headers })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setComplianceData(data); })
        .catch(() => {});
    }
    if (activeTab === "leads" && !leadsLoaded) {
      setLeadsLoaded(true);
      fetch(`${API_BASE}/api/niriksha/leads`, { headers })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setLeadsData(data.leads); })
        .catch(() => {});
    }
  }, [activeTab, accessToken, passportsLoaded, registryLoaded, revenueLoaded, complianceLoaded, leadsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Onboard lead ────────────────────────────────────────────────────────────
  const handleOnboard = useCallback(async (leadId) => {
    setOnboarding(prev => ({ ...prev, [leadId]: true }));
    setOnboardError(prev => ({ ...prev, [leadId]: null }));
    try {
      const resp = await fetch(`${API_BASE}/api/niriksha/leads/${leadId}/onboard`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await resp.json();
      if (resp.ok) {
        setLeadsData(prev => prev.map(l => l.id === leadId ? { ...l, status: "Onboarded" } : l));
      } else {
        setOnboardError(prev => ({ ...prev, [leadId]: data?.detail || "Onboard failed" }));
      }
    } catch {
      setOnboardError(prev => ({ ...prev, [leadId]: "Network error — try again" }));
    } finally {
      setOnboarding(prev => ({ ...prev, [leadId]: false }));
    }
  }, [accessToken]);

  // ── Force confirm passport ──────────────────────────────────────────────────
  const handleForceConfirm = useCallback(async (passportId) => {
    setConfirming(prev => ({ ...prev, [passportId]: true }));
    setConfirmMsg(prev => ({ ...prev, [passportId]: null }));
    try {
      const resp = await fetch(`${API_BASE}/api/niriksha/passports/${passportId}/force-confirm`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await resp.json();
      if (resp.ok) {
        setPassportsData(prev => prev?.map(pp =>
          pp.id === passportId ? { ...pp, status: "pending_confirmation" } : pp
        ));
        setConfirmMsg(prev => ({ ...prev, [passportId]: "✓ Confirmed" }));
      } else {
        setConfirmMsg(prev => ({ ...prev, [passportId]: data?.detail || "Failed" }));
      }
    } catch {
      setConfirmMsg(prev => ({ ...prev, [passportId]: "Network error" }));
    } finally {
      setConfirming(prev => ({ ...prev, [passportId]: false }));
    }
  }, [accessToken]);

  // ── Initial load ────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const client = getSupabaseClient();
        const { data: { session } } = await client.auth.getSession();
        if (!session) { navigate("/login", { replace: true }); return; }

        const token  = session.access_token;
        const userId = session.user.id;

        const { data: user, error: userErr } = await client
          .from("users").select("id, role, full_name")
          .eq("id", userId).single();
        if (userErr || !user) throw new Error("user-row-missing");

        if (!cancelled) {
          if (user.role !== "admin") {
            setPhase(PHASE.FORBIDDEN);
            return;
          }
          setAccessToken(token);
          await fetchHealth(token);
          await fetchPricing(token);
          setPhase(PHASE.READY);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("niriksha: load failed", err);
        if (!cancelled) setPhase(PHASE.ERROR);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [navigate, fetchHealth, fetchPricing]);

  // ── Refresh ─────────────────────────────────────────────────────────────────
  async function handleRefresh() {
    if (!accessToken || refreshing) return;
    setRefreshing(true);
    // Reset lazy-tab flags so each tab re-fetches on next visit (or immediately if active)
    setPassportsLoaded(false);
    setRegistryLoaded(false);
    setRevenueLoaded(false);
    setComplianceLoaded(false);
    setLeadsLoaded(false);
    await fetchHealth(accessToken);
    setRefreshing(false);
  }

  // ── Save pricing ─────────────────────────────────────────────────────────────
  async function handleSavePricing() {
    if (!accessToken || pricingSaving) return;
    setPricingSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/niriksha/pricing`, {
        method:  "PUT",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body:    JSON.stringify(pricingForm),
      });
      if (res.ok) {
        const updated = await res.json();
        setPricingData(updated);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("niriksha: pricing save failed", err);
    }
    setPricingSaving(false);
  }

  // ── Sign out ─────────────────────────────────────────────────────────────────
  async function handleSignOut() {
    try { await getSupabaseClient().auth.signOut(); } catch { /* swallow */ }
    navigate("/login", { replace: true });
  }

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (phase === PHASE.LOADING) {
    return (
      <Koshtha role="admin" accessToken={accessToken} onSignOut={handleSignOut}>
        <main className="tl-page tl-page--centered">
          <p className="tl-loading" aria-label="Loading Niriksha">Loading…</p>
        </main>
      </Koshtha>
    );
  }

  // ── Forbidden ───────────────────────────────────────────────────────────────
  if (phase === PHASE.FORBIDDEN) {
    return (
      <Koshtha role="admin" accessToken={accessToken} onSignOut={handleSignOut}>
        <main className="tl-page tl-page--centered">
          <p className="tl-error" aria-label="Access restricted">
            Access restricted. This page is for authorised administrators only.
          </p>
        </main>
      </Koshtha>
    );
  }

  // ── Error ───────────────────────────────────────────────────────────────────
  if (phase === PHASE.ERROR) {
    return (
      <Koshtha role="admin" accessToken={accessToken} onSignOut={handleSignOut}>
        <main className="tl-page tl-page--centered">
          <p className="tl-error" aria-label="Load error">
            Unable to load control centre. Contact support@terralekha.in
          </p>
        </main>
      </Koshtha>
    );
  }

  // ── Ready — derive all display values ────────────────────────────────────────
  const delivery        = health?.delivery        ?? {};
  const components      = health?.components      ?? {};
  const modules         = delivery.modules        ?? {};
  const pending         = delivery.pending        ?? [];
  const sprint4Tasks    = delivery.sprint4_tasks  ?? [];
  const overall         = health?.overall         ?? "—";

  // Hero KPI sources
  const db              = components.database_supabase ?? {};
  const orgCount        = db.org_count       ?? "—";
  const userCount       = db.user_count      ?? "—";
  const passportCount   = db.passport_count  ?? "—";
  const statusBreakdown = db.status_breakdown ?? {};
  const hasFunnel       = Object.keys(statusBreakdown).length > 0;
  const totalPassports  = typeof passportCount === "number" ? passportCount : 0;

  const sprintNum = delivery.sprint ?? 4;

  const lastFetchedStr = lastFetched
    ? lastFetched.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : "—";

  return (
    <Koshtha role="admin" accessToken={accessToken} onSignOut={handleSignOut} hideOra>

      <main className="tl-page tl-page--form">

        {/* ── Page header ── */}
        <div className="tl-nk-page-header">
          <div>
            <h1
              className="tl-nk-page-title"
              aria-label="Niriksha control centre heading"
            >
              Niriksha — Control Centre
            </h1>
            <p className="tl-nk-page-sub">
              Founder-only · Real-time TERRALEKHA health
            </p>
          </div>
          <div className="tl-nk-header-right">
            <span
              className={`tl-nk-overall-badge tl-nk-overall--${overall}`}
              aria-label={`Overall health: ${overall}`}
            >
              {overall.toUpperCase()}
            </span>
            <button
              className="tl-button-channel"
              onClick={handleRefresh}
              disabled={refreshing}
              aria-label="Refresh health data"
            >
              {refreshing ? "Refreshing…" : "↻ Refresh"}
            </button>
          </div>
        </div>

        <p className="tl-nk-last-fetched" aria-label={`Last fetched: ${lastFetchedStr}`}>
          Last fetched: {lastFetchedStr}
        </p>

        {/* ── Tab bar ── */}
        <div className="tl-nk-tabs" role="tablist">
          <button
            className={`tl-nk-tab ${activeTab === "health" ? "tl-nk-tab--active" : ""}`}
            role="tab" aria-selected={activeTab === "health"}
            aria-label="Open Health tab"
            onClick={() => setActiveTab("health")}
          >Health</button>
          <button
            className={`tl-nk-tab ${activeTab === "passports" ? "tl-nk-tab--active" : ""}`}
            role="tab" aria-selected={activeTab === "passports"}
            aria-label="Open PassPorts tab"
            onClick={() => setActiveTab("passports")}
          >PassPorts</button>
          <button
            className={`tl-nk-tab ${activeTab === "registry" ? "tl-nk-tab--active" : ""}`}
            role="tab" aria-selected={activeTab === "registry"}
            aria-label="Open Registry tab"
            onClick={() => setActiveTab("registry")}
          >Registry</button>
          <button
            className={`tl-nk-tab ${activeTab === "revenue" ? "tl-nk-tab--active" : ""}`}
            role="tab" aria-selected={activeTab === "revenue"}
            aria-label="Open Revenue tab"
            onClick={() => setActiveTab("revenue")}
          >Revenue</button>
          <button
            className={`tl-nk-tab ${activeTab === "pricing" ? "tl-nk-tab--active" : ""}`}
            role="tab" aria-selected={activeTab === "pricing"}
            aria-label="Open Pricing tab"
            onClick={() => setActiveTab("pricing")}
          >Pricing</button>
          <button
            className={`tl-nk-tab ${activeTab === "leads" ? "tl-nk-tab--active" : ""}`}
            role="tab" aria-selected={activeTab === "leads"}
            aria-controls="nk-leads-tab"
            onClick={() => setActiveTab("leads")}
          >Leads</button>
          <button
            className={`tl-nk-tab ${activeTab === "compliance" ? "tl-nk-tab--active" : ""}`}
            role="tab" aria-selected={activeTab === "compliance"}
            aria-label="Open Compliance tab"
            onClick={() => setActiveTab("compliance")}
          >Compliance</button>
        </div>

        {/* ── Health tab content ── */}
        {activeTab === "health" && (
        <>

        {/* ── 1. Hero KPI strip ── */}
        <section aria-labelledby="nk-hero-heading">
          <h2 id="nk-hero-heading" className="tl-nk-section-title">Product snapshot</h2>
          <div className="tl-nk-hero-strip" aria-label="Product KPI strip">
            <HeroKpi
              label="Organisations"
              value={orgCount}
              color="gold"
              ariaLabel="org count"
            />
            <HeroKpi
              label="Users"
              value={userCount}
              color="blue"
              ariaLabel="user count"
            />
            <HeroKpi
              label="PassPorts"
              value={passportCount}
              color="green"
              ariaLabel="passport count"
            />
            <HeroKpi
              label="Backend tests"
              value={delivery.tests_backend}
              color="purple"
              ariaLabel="backend test count"
            />
            <HeroKpi
              label="Portal tests"
              value={delivery.tests_portal}
              color="purple"
              ariaLabel="portal test count"
            />
            <HeroKpi
              label={`Sprint ${sprintNum} tasks`}
              value={`${delivery.tasks_done ?? "—"}/${delivery.tasks_total ?? "—"}`}
              color="gold"
              ariaLabel="tasks done"
            />
          </div>
        </section>

        {/* ── 2. Infrastructure health ── */}
        <section aria-labelledby="nk-comp-heading">
          <h2 id="nk-comp-heading" className="tl-nk-section-title">Infrastructure health</h2>
          <div className="tl-nk-comp-grid" aria-label="Component health grid">
            {Object.entries(components).map(([key, comp]) => (
              <ComponentCard key={key} name={key} data={comp} />
            ))}
          </div>
        </section>

        {/* ── 3. Sprint progress + Pending actions (two-column) ── */}
        <div className="tl-nk-two-col">

          <section aria-labelledby="nk-sprint-progress-heading">
            <h2 id="nk-sprint-progress-heading" className="tl-nk-section-title">
              Sprint {sprintNum} — task checklist
            </h2>
            <div className="tl-nk-sprint-summary">
              <span
                className={`tl-nk-pill ${delivery.sprint_status === "complete"
                  ? "tl-nk-pill--healthy"
                  : "tl-nk-pill--warn"}`}
                aria-label="sprint status"
              >
                {delivery.sprint_status ?? "—"}
              </span>
              <span className="tl-nk-sprint-fraction">
                {delivery.tasks_done ?? 0} of {delivery.tasks_total ?? 0} tasks done
              </span>
            </div>
            {sprint4Tasks.length > 0 && (
              <ul
                className="tl-nk-task-list"
                aria-label={`Sprint ${sprintNum} tasks`}
                role="list"
              >
                {sprint4Tasks.map((task) => (
                  <SprintTaskRow key={task.key} task={task} />
                ))}
              </ul>
            )}
          </section>

          {pending.length > 0 && (
            <section aria-labelledby="nk-pending-heading">
              <h2 id="nk-pending-heading" className="tl-nk-section-title">Pending actions</h2>
              <ul className="tl-nk-pending-list" role="list">
                {pending.map((item, i) => (
                  <li key={i} className="tl-nk-pending-item">
                    <span className="tl-nk-pending-icon" aria-hidden="true">!</span>
                    {item}
                  </li>
                ))}
              </ul>
            </section>
          )}

        </div>

        {/* ── 4. Module delivery table ── */}
        <section aria-labelledby="nk-modules-heading">
          <h2 id="nk-modules-heading" className="tl-nk-section-title">Module delivery</h2>
          <div className="tl-nk-table-wrap">
            <table className="tl-nk-table" aria-label="Module delivery table">
              <thead>
                <tr>
                  <th className="tl-nk-th">Module</th>
                  <th className="tl-nk-th">Sanskrit</th>
                  <th className="tl-nk-th">Tier</th>
                  <th className="tl-nk-th">Tests</th>
                  <th className="tl-nk-th">Status</th>
                  <th className="tl-nk-th">Note</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(modules).map(([key, mod]) => (
                  <ModuleRow key={key} modKey={key} mod={mod} />
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── 5. PassPort funnel ── (conditional — shown when Supabase returns data) */}
        {hasFunnel && (
          <section aria-labelledby="nk-funnel-heading">
            <h2 id="nk-funnel-heading" className="tl-nk-section-title">PassPort funnel</h2>
            <div className="tl-nk-funnel" aria-label="PassPort status funnel">
              {FUNNEL_STEPS.map(({ key, label }) => (
                <FunnelStep
                  key={key}
                  label={label}
                  count={statusBreakdown[key] ?? 0}
                  total={totalPassports}
                />
              ))}
            </div>
            <p className="tl-nk-funnel-note">
              PassPort FSM stages · total {totalPassports} · live from Supabase
            </p>
          </section>
        )}

        </>
        )}

        {/* ── PassPorts tab ── */}
        {activeTab === "passports" && (
          <section aria-labelledby="nk-passports-heading">
            <h2 id="nk-passports-heading" className="tl-nk-section-title">All PassPorts</h2>
            {!passportsData && (
              <p className="tl-loading" aria-label="PassPorts loading">Loading…</p>
            )}
            {passportsData && (
              <div className="tl-nk-table-wrap">
                <table className="tl-nk-table" aria-label="All PassPorts table">
                  <thead>
                    <tr>
                      <th className="tl-nk-th">CN Code</th>
                      <th className="tl-nk-th">GSTIN</th>
                      <th className="tl-nk-th">Period</th>
                      <th className="tl-nk-th">Status</th>
                      <th className="tl-nk-th">tCO₂e</th>
                      <th className="tl-nk-th">Paid</th>
                      <th className="tl-nk-th">Created</th>
                      <th className="tl-nk-th">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {passportsData.map((pp) => (
                      <tr key={pp.id} className="tl-nk-tr">
                        <td className="tl-nk-td">{pp.cn_code}</td>
                        <td className="tl-nk-td">{pp.gstin}</td>
                        <td className="tl-nk-td">
                          {pp.reporting_period_start} – {pp.reporting_period_end}
                        </td>
                        <td className="tl-nk-td">
                          <span
                            className={`tl-nk-pill ${statusClass(pp.status)}`}
                            aria-label={`Status: ${pp.status}`}
                          >
                            {pp.status}
                          </span>
                        </td>
                        <td className="tl-nk-td">{pp.total_tco2e ?? "—"}</td>
                        <td className="tl-nk-td">{pp.paid_at ? "✓" : "—"}</td>
                        <td className="tl-nk-td">{pp.created_at?.slice(0, 10) ?? "—"}</td>
                        <td className="tl-nk-td">
                          {pp.status === "draft" && (
                            <>
                              <button
                                style={{ padding: "3px 10px", borderRadius: "4px", border: "none", background: "#1A6B3C", color: "#fff", cursor: "pointer", fontSize: "0.75rem", opacity: confirming[pp.id] ? 0.6 : 1 }}
                                onClick={() => handleForceConfirm(pp.id)}
                                disabled={!!confirming[pp.id]}
                                aria-label={`Force confirm passport ${pp.id}`}
                              >
                                {confirming[pp.id] ? "…" : "Force Confirm"}
                              </button>
                              {confirmMsg[pp.id] && (
                                <p style={{ fontSize: "0.7rem", marginTop: "3px", color: confirmMsg[pp.id].startsWith("✓") ? "#1A6B3C" : "#c0392b" }}>
                                  {confirmMsg[pp.id]}
                                </p>
                              )}
                            </>
                          )}
                          {pp.status !== "draft" && <span className="tl-nk-pill tl-nk-pill--muted">{pp.status}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* ── Registry tab ── */}
        {activeTab === "registry" && (
          <section aria-labelledby="nk-registry-heading">
            <h2 id="nk-registry-heading" className="tl-nk-section-title">Registry</h2>
            {!registryData && (
              <p className="tl-loading" aria-label="Registry loading">Loading…</p>
            )}
            {registryData && (
              <>
                <h3 className="tl-nk-section-title" style={{ fontSize: "1rem" }}>Organisations</h3>
                <div
                  className="tl-nk-comp-grid"
                  aria-label="Organisations list"
                  style={{ marginBottom: "2rem" }}
                >
                  {registryData.organisations.map((org) => (
                    <div key={org.gstin} className="tl-nk-comp-card">
                      <div className="tl-nk-comp-header">
                        <span className="tl-nk-comp-name">{org.legal_name}</span>
                        <span className="tl-nk-pill tl-nk-pill--info">{org.sector}</span>
                      </div>
                      <p className="tl-nk-comp-meta">{org.gstin}</p>
                      <p className="tl-nk-comp-meta">{org.city}, {org.state_code}</p>
                    </div>
                  ))}
                </div>

                <h3 className="tl-nk-section-title" style={{ fontSize: "1rem", marginTop: "2rem" }}>Users</h3>
                <div className="tl-nk-table-wrap">
                  <table className="tl-nk-table" aria-label="Users table">
                    <thead>
                      <tr>
                        <th className="tl-nk-th">Email</th>
                        <th className="tl-nk-th">Mobile</th>
                        <th className="tl-nk-th">GSTIN</th>
                        <th className="tl-nk-th">Role</th>
                        <th className="tl-nk-th">Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registryData.users.map((u) => (
                        <tr key={u.id} className="tl-nk-tr">
                          <td className="tl-nk-td">{u.email}</td>
                          <td className="tl-nk-td">{u.mobile}</td>
                          <td className="tl-nk-td">{u.gstin}</td>
                          <td className="tl-nk-td">
                            <span className="tl-nk-pill tl-nk-pill--info">{u.role}</span>
                          </td>
                          <td className="tl-nk-td">{u.is_active ? "✓" : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        )}

        {/* ── Revenue tab ── */}
        {activeTab === "revenue" && (
          <section aria-labelledby="nk-revenue-heading">
            <h2 id="nk-revenue-heading" className="tl-nk-section-title">Revenue</h2>
            {!revenueData && (
              <p className="tl-loading" aria-label="Revenue loading">Loading…</p>
            )}
            {revenueData && (
              <>
                {/* KPI strip */}
                <div className="tl-nk-hero-strip" aria-label="Revenue KPI strip">
                  <HeroKpi
                    label="Total Revenue"
                    value={`₹${Math.round(revenueData.total_revenue_paise / 100)}`}
                    color="gold"
                    ariaLabel="total revenue"
                  />
                  <HeroKpi
                    label="Active Subscriptions"
                    value={revenueData.active_subscriptions}
                    color="green"
                    ariaLabel="active subscriptions"
                  />
                  <HeroKpi
                    label="Per-PassPort Sales"
                    value={revenueData.per_passport_payments}
                    color="blue"
                    ariaLabel="per passport payments"
                  />
                  <HeroKpi
                    label="Total Transactions"
                    value={revenueData.total_payments}
                    color="purple"
                    ariaLabel="total transactions"
                  />
                </div>

                {/* Recent payments table */}
                <div className="tl-nk-table-wrap" style={{ marginTop: "1.5rem" }}>
                  <table className="tl-nk-table" aria-label="Recent payments table">
                    <thead>
                      <tr>
                        <th className="tl-nk-th">GSTIN</th>
                        <th className="tl-nk-th">Type</th>
                        <th className="tl-nk-th">Amount</th>
                        <th className="tl-nk-th">Status</th>
                        <th className="tl-nk-th">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revenueData.recent_payments.map((p, i) => (
                        <tr key={i} className="tl-nk-tr">
                          <td className="tl-nk-td">{p.gstin}</td>
                          <td className="tl-nk-td">{p.payment_type}</td>
                          <td
                            className="tl-nk-td"
                            aria-label={`Amount: ₹${Math.round(p.amount_paise / 100)}`}
                          >
                            ₹{Math.round(p.amount_paise / 100)}
                          </td>
                          <td className="tl-nk-td">
                            <span className={`tl-nk-pill ${statusClass(p.status)}`}>
                              {p.status}
                            </span>
                          </td>
                          <td className="tl-nk-td">
                            {p.verified_at?.slice(0, 10) ?? "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        )}

        {/* ── Pricing tab ── */}
        {activeTab === "pricing" && (
          <section aria-labelledby="nk-pricing-heading">
            <h2 id="nk-pricing-heading" className="tl-nk-section-title">Pricing Configuration</h2>

            {pricingData && (
              <div className="tl-nk-pricing-current">
                <p>
                  Per PassPort:{" "}
                  <strong aria-label="Current per-passport price">
                    ₹{Math.round(pricingData.per_passport_price / 100)}
                  </strong>
                </p>
                <p>
                  Monthly subscription:{" "}
                  <strong aria-label="Current monthly price">
                    ₹{Math.round(pricingData.subscription_monthly_price / 100)}/month
                  </strong>
                </p>
                <p>
                  Annual subscription:{" "}
                  <strong aria-label="Current annual price">
                    ₹{Math.round(pricingData.subscription_annual_price / 100)}/year
                  </strong>
                </p>
              </div>
            )}

            {pricingForm && (
              <div className="tl-nk-pricing-form">
                <label className="tl-nk-pricing-label">
                  <span>Per-passport price (₹)</span>
                  <input
                    className="tl-nk-pricing-input"
                    type="number"
                    aria-label="Per-passport price (₹)"
                    value={Math.round((pricingForm.per_passport_price ?? 0) / 100)}
                    onChange={(e) => setPricingForm((f) => ({
                      ...f, per_passport_price: Math.round(Number(e.target.value) * 100)
                    }))}
                  />
                </label>
                <label className="tl-nk-pricing-label">
                  <span>Monthly subscription (₹)</span>
                  <input
                    className="tl-nk-pricing-input"
                    type="number"
                    aria-label="Monthly subscription price (₹)"
                    value={Math.round((pricingForm.subscription_monthly_price ?? 0) / 100)}
                    onChange={(e) => setPricingForm((f) => ({
                      ...f, subscription_monthly_price: Math.round(Number(e.target.value) * 100)
                    }))}
                  />
                </label>
                <label className="tl-nk-pricing-label">
                  <span>Annual subscription (₹)</span>
                  <input
                    className="tl-nk-pricing-input"
                    type="number"
                    aria-label="Annual subscription price (₹)"
                    value={Math.round((pricingForm.subscription_annual_price ?? 0) / 100)}
                    onChange={(e) => setPricingForm((f) => ({
                      ...f, subscription_annual_price: Math.round(Number(e.target.value) * 100)
                    }))}
                  />
                </label>
                <button
                  className="tl-button-channel"
                  aria-label="Save pricing"
                  disabled={pricingSaving}
                  onClick={handleSavePricing}
                >
                  {pricingSaving ? "Saving…" : "Save Pricing"}
                </button>
              </div>
            )}
          </section>
        )}

        {/* ── Leads tab ── */}
        {activeTab === "leads" && (
          <section id="nk-leads-tab" aria-labelledby="nk-leads-heading">
            <h2 id="nk-leads-heading" className="tl-nk-section-title">Leads</h2>
            <p className="tl-nk-comp-meta" style={{ marginBottom: "1rem" }}>
              Registration form submissions. Click <strong>Onboard →</strong> to create a portal account and send a magic link sign-in email.
            </p>
            {!leadsData && <p className="tl-loading" aria-label="Leads loading">Loading…</p>}
            {leadsData && leadsData.length === 0 && (
              <p className="tl-nk-comp-meta">No leads yet.</p>
            )}
            {leadsData && leadsData.length > 0 && (
              <div className="tl-nk-table-wrap">
                <table className="tl-nk-table" aria-label="Leads table">
                  <thead>
                    <tr>
                      <th className="tl-nk-th">Name</th>
                      <th className="tl-nk-th">Company</th>
                      <th className="tl-nk-th">Email</th>
                      <th className="tl-nk-th">GSTIN</th>
                      <th className="tl-nk-th">Audience</th>
                      <th className="tl-nk-th">Status</th>
                      <th className="tl-nk-th">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leadsData.map((lead) => (
                      <tr key={lead.id} className="tl-nk-tr">
                        <td className="tl-nk-td">{lead.name}</td>
                        <td className="tl-nk-td">{lead.company}</td>
                        <td className="tl-nk-td" style={{ fontSize: "0.8rem" }}>{lead.email}</td>
                        <td className="tl-nk-td">{lead.gstin || "—"}</td>
                        <td className="tl-nk-td">{lead.audience || "—"}</td>
                        <td className="tl-nk-td">
                          <span
                            className={`tl-nk-pill ${lead.status === "Onboarded" ? "tl-nk-pill--healthy" : "tl-nk-pill--warn"}`}
                            aria-label={`Lead status: ${lead.status}`}
                          >{lead.status}</span>
                        </td>
                        <td className="tl-nk-td">
                          {lead.gstin ? (
                            <>
                              <button
                                style={{
                                  padding: "4px 12px", borderRadius: "4px", border: "none",
                                  background: lead.status === "Onboarded" ? "#555" : "#1A6B3C",
                                  color: "#fff", cursor: "pointer", fontSize: "0.8rem",
                                  opacity: onboarding[lead.id] ? 0.6 : 1,
                                }}
                                onClick={() => handleOnboard(lead.id)}
                                disabled={!!onboarding[lead.id]}
                                aria-label={lead.status === "Onboarded" ? `Resend magic link to ${lead.email}` : `Onboard ${lead.email}`}
                              >
                                {onboarding[lead.id]
                                  ? "Sending…"
                                  : lead.status === "Onboarded" ? "Resend Link" : "Onboard →"}
                              </button>
                              {onboardError[lead.id] && (
                                <p style={{ color: "#c0392b", fontSize: "0.72rem", marginTop: "4px" }}>
                                  {onboardError[lead.id]}
                                </p>
                              )}
                            </>
                          ) : (
                            <span className="tl-nk-pill tl-nk-pill--muted">Manual</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* ── Compliance tab ── */}
        {activeTab === "compliance" && (
          <section aria-labelledby="nk-compliance-heading">
            <h2 id="nk-compliance-heading" className="tl-nk-section-title">Compliance Filing Status</h2>
            {!complianceData && (
              <p className="tl-loading" aria-label="Compliance loading">Loading…</p>
            )}
            {complianceData && (
              <>
                <div className="tl-nk-hero-strip" aria-label="Compliance KPI strip">
                  <HeroKpi label="Total Filings" value={complianceData.total_passports} color="gold"   ariaLabel="total passports" />
                  <HeroKpi label="Certified"      value={complianceData.total_certified}  color="green"  ariaLabel="total certified" />
                  <HeroKpi label="Pending"         value={complianceData.total_pending}    color="purple" ariaLabel="total pending" />
                </div>
                <div className="tl-nk-comp-grid">
                  {complianceData.frameworks.map((fw) => (
                    <div
                      key={fw.compliance_type}
                      className="tl-nk-comp-card"
                      aria-label={`${fw.compliance_type} compliance card`}
                    >
                      <div className="tl-nk-comp-header">
                        <span className="tl-nk-comp-name">{fw.compliance_type}</span>
                        <span className="tl-nk-pill tl-nk-pill--info">{fw.compliance_type}</span>
                      </div>
                      <p className="tl-nk-comp-meta">Total filings: {fw.total_passports}</p>
                      <p className="tl-nk-comp-meta">Certified: {fw.certified}</p>
                      <p className="tl-nk-comp-meta">Pending: {fw.pending_certification}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        )}

      </main>
    </Koshtha>
  );
}
